import { useMutation, useQuery } from "react-query";
import { api } from "@data-phone/react-resource-server-auth";
import { AppDispatch } from "../../store/store";
import { useDispatch } from "react-redux";
import { setBulkMessages } from "../../store/slices/bulk_messages.slice";
import qs from "qs";

export interface OutgoingBulkMessage {
    to: string;
    text: string;
}

export interface BulkMessage {
    id: string;
    did: string;
    label: string;
    first_name: string;
    last_name: string;
    total: number;
    sent: number;
    failed: number;
    pending: number;
    scheduled: number;
    created_at: string;
}

export interface BulkMessageData {
    bulk_message_id: string;
    status: string;
    participant: string;
    timestamp: string;
    scheduled_at?: string;
    body: string;
}

export interface BulkMessageStatus {
    bulk_message_id: string;
    status: string;
    participant: string;
    updated_at: string;
    body: string;
}

export interface BulkSMSReportQuery {
    offset: number;
    limit: number;
    start_date: Date;
    end_date: Date;
    start_time: Date | null;
    end_time: Date | null;
    user: string | null;
    account_id?: string;
    dids: string[] | null;
    searchInput: string | null;
}

export interface BulkMessageStatistics {
    total: number;
    total_bulk_jobs?: number;
    sent: number;
    failed: number;
    pending: number;
    scheduled: number;
}

const getBulkMessagesApi = async (query: BulkSMSReportQuery, dispatch: AppDispatch) => {
    const response: {
        data: {
            messages: BulkMessage[];
            statistics: BulkMessageStatistics;
        };
    } = await api.get(`${process.env.REACT_APP_SMS_API_URL}/bulk?${qs.stringify({ ...query })}`);
    dispatch(setBulkMessages(response.data.messages));
    return response.data ;
};

const getBulkMessageDetailsApi = async (id: string): Promise<BulkMessageData[]> => {
    const response = await api.get(`${process.env.REACT_APP_SMS_API_URL}/bulk/${id}`);
    return response.data;
};

const sendBulkMessageApi = async (data: { messages: OutgoingBulkMessage[]; from: string }) => {
    const response = await api.post(`${process.env.REACT_APP_SMS_API_URL}/sms/send-bulk`, data);
    return response.data;
};

export const useSendBulkMessage = () => {
    return useMutation(
        (data: { messages: OutgoingBulkMessage[]; from: string; label: string; scheduled_at?: Date }) => sendBulkMessageApi(data),
        {}
    );
};

export const useGetBulkMessagesJobs = (query: BulkSMSReportQuery) => {
    const dispatch: AppDispatch = useDispatch();

    return useQuery<{
        messages: BulkMessage[];
        statistics: BulkMessageStatistics;
    }, Error>({
        queryKey: ["bulkmessages", query],
        queryFn: () => getBulkMessagesApi(query, dispatch),
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.messages.length > 0 ? allPages.length * 20 : undefined;
        },
        enabled: false
    });
};

export const useGetBulkMessageDetails = (id: string) => {
    return useQuery<BulkMessageData[], Error>({
        queryKey: ["bulkmessages", id],
        queryFn: () => getBulkMessageDetailsApi(id)
    });
};
